import CopyrightIcon from "@mui/icons-material/Copyright";
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, Grow } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import {
  ThemeScope, Button, Input, Label, Card, CardHeader, CardTitle, CardDescription,
  CardContent, CardFooter, Alert, Badge, Separator,
} from "./ui/shadcn";

// ── Design tokens (dark navy + neon teal) ──
const C = {
  bg: "#04091a",
  card: "#0a1330",
  cardTop: "#0d1838",
  text: "#e8f1ff",
  muted: "#93a8c8",
  faint: "#6f86aa",
  line: "rgba(148,163,184,0.2)",
  teal: "#2dd4bf",
  tealSoft: "#5eead4",
  cyan: "#22d3ee",
  warn: "#fbbf24",
  err: "#fca5a5",
};

const FONT = "'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif";

// shadcn tokens for this concept (applied through ThemeScope)
const THEME_VARS = {
  "--background": C.bg,
  "--foreground": C.text,
  "--card": C.card,
  "--card-foreground": C.text,
  "--primary": C.teal,
  "--primary-foreground": "#031318",
  "--secondary": "rgba(255,255,255,0.06)",
  "--secondary-foreground": C.text,
  "--muted": "rgba(255,255,255,0.05)",
  "--muted-foreground": C.muted,
  "--accent": "rgba(45,212,191,0.12)",
  "--destructive": "#f87171",
  "--destructive-foreground": "#ffffff",
  "--border": C.line,
  "--input": C.line,
  "--ring": C.teal,
  "--radius": "0.85rem",
};

// Static twinkling "stars" for the backdrop: [top%, left%, size px, delay s]
const STARS = [
  [8, 12, 2, 0], [14, 78, 3, 1.2], [22, 38, 2, 2.1], [31, 90, 2, 0.6], [44, 6, 3, 1.8],
  [58, 84, 2, 2.6], [66, 22, 2, 0.9], [74, 66, 3, 1.5], [83, 40, 2, 2.9], [90, 92, 2, 0.3],
  [12, 55, 2, 3.2], [49, 48, 2, 1.1], [93, 14, 3, 2.4], [27, 68, 2, 0.4],
];

const EASE = [0.22, 1, 0.36, 1];

// Motion-enabled shadcn primitives
const MotionButton = motion.create(Button);
const MotionCard = motion.create(Card);

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');
.ld04-root, .ld04-root * { font-family: ${FONT} !important; }

@keyframes ld04Aurora1 { 0%,100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(60px,40px,0) scale(1.12); } }
@keyframes ld04Aurora2 { 0%,100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(-70px,-30px,0) scale(1.1); } }
@keyframes ld04Grid { from { background-position: 0 0, 0 0; } to { background-position: 56px 56px, 56px 56px; } }
@keyframes ld04Twinkle { 0%,100% { opacity: .15; transform: scale(.8); } 50% { opacity: .9; transform: scale(1.25); } }
@keyframes ld04Pulse { 0%,100% { opacity: .35; transform: scaleX(.92); } 50% { opacity: .6; transform: scaleX(1.04); } }
@keyframes ld04Dot { 0% { box-shadow: 0 0 0 0 rgba(45,212,191,.55); } 70% { box-shadow: 0 0 0 7px rgba(45,212,191,0); } 100% { box-shadow: 0 0 0 0 rgba(45,212,191,0); } }
@keyframes ld04Sweep { from { left: -60%; } to { left: 130%; } }

/* ── page + backdrop ── */
.ld04-page { position: relative; height: 100vh; height: 100dvh; width: 100%; overflow-x: hidden; overflow-y: auto; display: flex; flex-direction: column; align-items: center; padding: clamp(16px, 3vw, 32px) clamp(16px, 4vw, 32px); padding-left: max(clamp(16px, 4vw, 32px), env(safe-area-inset-left)); padding-right: max(clamp(16px, 4vw, 32px), env(safe-area-inset-right)); padding-bottom: max(clamp(16px, 3vw, 32px), env(safe-area-inset-bottom)); background-color: ${C.bg}; background-image: radial-gradient(ellipse at 50% -10%, #0b1c4a 0%, #060d26 45%, #03081a 100%); background-attachment: fixed; }
.ld04-bg { position: fixed; inset: 0; overflow: hidden; pointer-events: none; z-index: 0; }
.ld04-grid { position: absolute; inset: 0; opacity: .55; background-image: linear-gradient(rgba(45,212,191,.07) 1px, transparent 1px), linear-gradient(90deg, rgba(45,212,191,.07) 1px, transparent 1px); background-size: 56px 56px, 56px 56px; -webkit-mask-image: radial-gradient(ellipse at 50% 45%, #000 8%, transparent 72%); mask-image: radial-gradient(ellipse at 50% 45%, #000 8%, transparent 72%); animation: ld04Grid 40s linear infinite; }
.ld04-aurora { position: absolute; border-radius: 50%; width: clamp(360px, 50vw, 700px); height: clamp(360px, 50vw, 700px); filter: blur(10px); }
.ld04-aurora-a { top: -18%; left: -12%; background: radial-gradient(circle, rgba(45,212,191,.24) 0%, transparent 66%); animation: ld04Aurora1 22s ease-in-out infinite; }
.ld04-aurora-b { bottom: -22%; right: -14%; background: radial-gradient(circle, rgba(99,102,241,.26) 0%, transparent 66%); animation: ld04Aurora2 26s ease-in-out infinite; }
.ld04-star { position: absolute; border-radius: 50%; }

.ld04-stage { position: relative; z-index: 1; width: 100%; min-width: 0; max-width: clamp(420px, 34vw, 520px); margin-block: auto; display: flex; flex-direction: column; align-items: center; flex-shrink: 0; }

/* ── brand ── */
.ld04-brand { display: flex; flex-direction: column; align-items: center; margin-bottom: clamp(18px, 3vh, 28px); width: 100%; min-width: 0; }
.ld04-logo { height: clamp(38px, 5vw, 46px); max-width: 70%; filter: brightness(0) invert(1) drop-shadow(0 0 14px rgba(45,212,191,.45)); }
.ld04-root .ld04-pill { margin-top: 14px; max-width: 100%; gap: 8px; padding: 6px 14px; border-color: rgba(45,212,191,.28); background: rgba(45,212,191,.07); color: ${C.tealSoft}; font-size: .68rem; font-weight: 600; line-height: 1.4; letter-spacing: clamp(1px, .3vw, 1.8px); text-transform: uppercase; text-align: center; white-space: normal; }
.ld04-pill-dot { width: 7px; height: 7px; border-radius: 50%; background: ${C.teal}; flex-shrink: 0; animation: ld04Dot 2.2s ease-out infinite; }

/* ── floating card with gradient border ── */
.ld04-card-wrap { position: relative; width: 100%; min-width: 0; }
.ld04-ground { position: absolute; z-index: -1; left: 8%; right: 8%; bottom: -34px; height: 56px; border-radius: 50%; background: radial-gradient(ellipse, rgba(45,212,191,.55) 0%, transparent 70%); filter: blur(22px); animation: ld04Pulse 7s ease-in-out infinite; pointer-events: none; }
.ld04-frame { position: relative; z-index: 1; padding: 1px; border-radius: 26px; background: linear-gradient(135deg, rgba(45,212,191,.85) 0%, rgba(56,189,248,.18) 28%, rgba(99,102,241,.12) 55%, rgba(99,102,241,.7) 100%); background-size: 260% 260%; box-shadow: 0 30px 80px -20px rgba(0,0,0,.8), 0 0 60px -10px rgba(45,212,191,.18); }
.ld04-root .ld04-card { border: 0; border-radius: 25px; background: linear-gradient(180deg, ${C.cardTop} 0%, ${C.card} 100%); box-shadow: inset 0 1px 0 rgba(255,255,255,.06); }
.ld04-root .ld04-ch { padding: clamp(26px, 4.5vw, 40px) clamp(20px, 4.5vw, 40px) 0; gap: 0; }
.ld04-root .ld04-cc { padding: clamp(22px, 3.5vw, 28px) clamp(20px, 4.5vw, 40px) 0; }
.ld04-root .ld04-cf { padding: clamp(18px, 3vw, 24px) clamp(20px, 4.5vw, 40px) clamp(22px, 3.5vw, 30px); flex-direction: column; align-items: stretch; gap: 0; }
.ld04-root .ld04-ch > *, .ld04-root .ld04-cc > *, .ld04-root .ld04-cf > * { min-width: 0; }
.ld04-root .ld04-title { font-size: clamp(1.5rem, 1.25rem + 1vw, 1.95rem); overflow-wrap: anywhere; font-weight: 700; letter-spacing: -.6px; line-height: 1.2; margin-bottom: 8px; color: ${C.text}; }
.ld04-grad { background: linear-gradient(90deg, #5eead4 0%, #22d3ee 100%); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: ${C.tealSoft}; }
.ld04-root .ld04-desc { font-size: .9rem; line-height: 1.6; color: ${C.muted}; }

/* ── fields ── */
.ld04-root .ld04-label { margin-bottom: 7px; color: ${C.muted}; font-size: .76rem; font-weight: 600; letter-spacing: .6px; text-transform: uppercase; line-height: 1.2; }
.ld04-label-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
.ld04-field { position: relative; }
.ld04-root .ld04-ico { position: absolute; left: 14px; top: 50%; margin-top: -9.5px; width: 19px; height: 19px; font-size: 19px; color: ${C.faint}; pointer-events: none; transition: color .25s ease, filter .25s ease; }
.ld04-field:focus-within .ld04-ico { color: ${C.teal}; filter: drop-shadow(0 0 5px rgba(45,212,191,.8)); }
.ld04-root .ld04-input.ui-input { height: 52px; padding: 0 14px 0 44px; border: 1px solid rgba(148,163,184,.4); border-radius: 12px; background-color: #101d42; color: ${C.text}; -webkit-text-fill-color: ${C.text}; opacity: 1; font-size: .94rem; caret-color: ${C.teal}; transition: background-color .25s ease, box-shadow .3s ease, border-color .25s ease; }
.ld04-root .ld04-input.ui-input::placeholder { color: ${C.muted}; -webkit-text-fill-color: ${C.muted}; opacity: 1; }
.ld04-root .ld04-input.ui-input:hover { border-color: rgba(45,212,191,.6); background-color: #12224c; }
.ld04-root .ld04-input.ui-input:focus, .ld04-root .ld04-input.ui-input:focus-visible { border-color: ${C.teal}; background-color: #0f2a4a; box-shadow: 0 0 0 3px rgba(45,212,191,.2), 0 0 26px rgba(45,212,191,.34); }
.ld04-root .ld04-input.ui-input:-webkit-autofill,
.ld04-root .ld04-input.ui-input:-webkit-autofill:hover { -webkit-text-fill-color: ${C.text}; color: ${C.text}; caret-color: ${C.teal}; -webkit-box-shadow: 0 0 0 1000px #101d42 inset; box-shadow: 0 0 0 1000px #101d42 inset; transition: background-color 9999s ease-out 0s; }
.ld04-root .ld04-input.ui-input:-webkit-autofill:focus { -webkit-text-fill-color: ${C.text}; color: ${C.text}; caret-color: ${C.teal}; -webkit-box-shadow: 0 0 0 1000px #0f2a4a inset, 0 0 0 3px rgba(45,212,191,.2), 0 0 26px rgba(45,212,191,.34); box-shadow: 0 0 0 1000px #0f2a4a inset, 0 0 0 3px rgba(45,212,191,.2), 0 0 26px rgba(45,212,191,.34); transition: background-color 9999s ease-out 0s; }
.ld04-root .ld04-input-pw.ui-input { padding-right: 84px; }
.ld04-root .ld04-input-id.ui-input { padding-right: 44px; }
.ld04-root .ld04-warn-ico { position: absolute; top: 50%; margin-top: -10px; width: 20px; height: 20px; font-size: 20px; color: ${C.warn}; pointer-events: none; }
.ld04-root .ld04-eye.ui-btn { position: absolute; right: 4px; top: 50%; margin-top: -22px; height: 44px; width: 44px; min-width: 44px; border-radius: 10px; color: ${C.muted}; }
.ld04-root .ld04-eye.ui-btn:hover { color: ${C.teal}; background: rgba(45,212,191,.1); }
.ld04-help { min-height: 18px; margin: 5px 0 4px 4px; font-size: 12px; line-height: 1.5; color: ${C.warn}; display: flex; align-items: center; gap: 4px; }
.ld04-forgot-gap { margin-bottom: 14px; }

.ld04-link { display: inline-flex; align-items: center; min-height: 44px; padding: 0 4px; margin: 0 -4px; color: ${C.tealSoft}; text-decoration: none; font-weight: 600; border-radius: 4px; transition: color .2s ease, text-shadow .2s ease; }
.ld04-link:hover { color: #99f6e4; text-shadow: 0 0 12px rgba(45,212,191,.7); text-decoration: underline; text-underline-offset: 3px; }
.ld04-link:focus-visible { outline: 2px solid ${C.teal}; outline-offset: 3px; }

/* ── alert ── */
.ld04-alert-wrap { overflow: hidden; }
.ld04-root .ld04-alert { display: flex; align-items: center; gap: 8px; overflow-wrap: anywhere; word-break: break-word; background: rgba(248,113,113,.1); border-color: rgba(248,113,113,.4); color: ${C.err}; padding: 12px 14px; border-radius: 12px; font-size: .83rem; line-height: 1.5; }
.ld04-alert-ico { font-size: 17px !important; flex-shrink: 0; }

/* ── buttons ── */
.ld04-root .ld04-btn-primary.ui-btn { width: 100%; height: 52px; border: 0; border-radius: 12px; font-size: .95rem; font-weight: 700; letter-spacing: .3px; color: #031318; position: relative; overflow: hidden; background: linear-gradient(135deg, #2dd4bf 0%, #22d3ee 100%); box-shadow: 0 0 0 1px rgba(94,234,212,.5), 0 8px 26px rgba(45,212,191,.32); transition: box-shadow .25s ease, filter .25s ease, background .2s ease, opacity .2s ease; }
.ld04-root .ld04-btn-primary.ui-btn::after { content: ""; position: absolute; top: 0; bottom: 0; left: -60%; width: 40%; background: linear-gradient(100deg, transparent, rgba(255,255,255,.55), transparent); transform: skewX(-20deg); opacity: 0; pointer-events: none; }
.ld04-root .ld04-btn-primary.ui-btn:hover:not(:disabled) { opacity: 1; background: linear-gradient(135deg, #5eead4 0%, #22d3ee 100%); box-shadow: 0 0 0 1px rgba(94,234,212,.8), 0 12px 34px rgba(45,212,191,.5); }
.ld04-root .ld04-btn-primary.ui-btn:hover:not(:disabled)::after { opacity: 1; animation: ld04Sweep .9s ease; }
.ld04-root .ld04-btn-primary.ui-btn:active { transform: none; }
.ld04-root .ld04-btn-primary.ui-btn:disabled { opacity: 1; color: #c9fff7; background: rgba(45,212,191,.28); box-shadow: none; }
.ld04-btn-inner { display: inline-flex; align-items: center; gap: 8px; }
.ld04-root .ld04-btn-ghost-teal.ui-btn { width: 100%; height: 48px; margin-top: 12px; border-radius: 12px; font-weight: 600; border-color: rgba(45,212,191,.4); background: transparent; color: ${C.tealSoft}; transition: box-shadow .25s ease, background-color .2s ease, border-color .2s ease; }
.ld04-root .ld04-btn-ghost-teal.ui-btn:hover { border-color: ${C.teal}; background: rgba(45,212,191,.1); box-shadow: 0 0 20px rgba(45,212,191,.2); }
.ld04-root .ui-btn:active { transform: none; }

.ld04-root .ld04-sep.ui-sep { background: rgba(148,163,184,.14); }
.ld04-signup { text-align: center; color: ${C.muted}; font-size: .875rem; line-height: 1.5; margin: 12px 0 0; overflow-wrap: anywhere; }

/* ── organization cards ── */
.ld04-orgs { display: flex; flex-direction: column; gap: 10px; max-height: max(176px, min(320px, 45vh)); max-height: max(176px, min(320px, 45dvh)); overflow-x: hidden; overflow-y: auto; padding: 4px; margin: -4px -4px 20px; min-width: 0; }
.ld04-org { display: block; position: relative; cursor: pointer; }
.ld04-org-input { position: absolute; opacity: 0; inset: 0; width: 100%; height: 100%; margin: 0; cursor: pointer; }
.ld04-root .ld04-org-card { display: flex; align-items: center; gap: 12px; min-width: 0; min-height: 60px; padding: 12px 14px; border-radius: 14px; background: rgba(255,255,255,.03); border: 1px solid ${C.line}; box-shadow: none; transition: border-color .2s ease, background-color .2s ease, box-shadow .25s ease; }
.ld04-org:hover .ld04-org-card { border-color: rgba(45,212,191,.55); }
.ld04-org-input:checked + .ld04-org-card { border-color: ${C.teal}; background: rgba(45,212,191,.08); box-shadow: 0 0 0 3px rgba(45,212,191,.14), 0 0 26px rgba(45,212,191,.28); }
.ld04-org-input:focus-visible + .ld04-org-card { box-shadow: 0 0 0 2px ${C.bg}, 0 0 0 4px ${C.teal}; }
.ld04-org-ico { width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: rgba(45,212,191,.1); border: 1px solid rgba(45,212,191,.25); color: ${C.teal}; }
.ld04-org-name { flex: 1; min-width: 0; font-size: .92rem; font-weight: 600; color: ${C.text}; overflow-wrap: anywhere; }
.ld04-radio { width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border: 1.5px solid rgba(148,163,184,.4); color: transparent; transition: all .2s ease; }
.ld04-org-input:checked + .ld04-org-card .ld04-radio { background: ${C.teal}; border-color: ${C.teal}; color: #031318; box-shadow: 0 0 12px rgba(45,212,191,.6); }

/* ── feature chips + footer ── */
.ld04-chips { display: none; flex-wrap: wrap; justify-content: center; gap: 8px; list-style: none; margin: clamp(32px, 5vh, 48px) 0 0; padding: 0; width: min(720px, calc(100vw - 64px)); }
@media (min-width: 600px) { .ld04-chips { display: flex; } }
.ld04-root .ld04-chip { gap: 7px; padding: 6px 12px; border-color: rgba(148,163,184,.18); background: rgba(255,255,255,.03); color: ${C.muted}; font-size: .76rem; font-weight: 500; transition: border-color .25s ease, background-color .25s ease; }
.ld04-root .ld04-chip:hover { border-color: rgba(45,212,191,.5); background: rgba(45,212,191,.07); }
.ld04-chip-check { width: 16px; height: 16px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; background: rgba(45,212,191,.16); border: 1px solid rgba(45,212,191,.6); color: ${C.teal}; }
.ld04-footer { position: relative; z-index: 1; margin: clamp(20px, 4vh, 32px) 0 0; flex-shrink: 0; max-width: 100%; padding-bottom: 4px; color: ${C.faint}; font-size: .73rem; display: flex; align-items: center; justify-content: center; gap: 6px; flex-wrap: wrap; text-align: center; }

/* ── session dialog ── */
.ld04-dlg-banner { background: linear-gradient(135deg, #0b3b4a 0%, #0f1f4d 60%, #1b1b57 100%); border-bottom: 1px solid rgba(45,212,191,.22); padding: 24px 24px 20px; }
.ld04-dlg-head { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.ld04-dlg-badge { width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: rgba(45,212,191,.14); border: 1px solid rgba(45,212,191,.5); box-shadow: 0 0 18px rgba(45,212,191,.35); color: ${C.tealSoft}; }
.ld04-dlg-title { margin: 0; font-size: 1.05rem; font-weight: 700; line-height: 1.3; color: #fff; }
.ld04-dlg-sub { margin: 2px 0 0; font-size: .78rem; color: rgba(226,240,255,.78); }
.ld04-dlg-note { margin: 0; padding: 8px 16px; border-radius: 10px; background: rgba(255,255,255,.07); border: 1px solid rgba(255,255,255,.08); font-size: .8rem; line-height: 1.5; color: rgba(226,240,255,.88); }
.ld04-dlg-body { padding: 16px 20px 8px; }
.ld04-dlg-eyebrow { margin: 0 0 12px; font-size: .72rem; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; color: ${C.muted}; }
.ld04-root .ld04-sess { display: flex; align-items: center; gap: 12px; padding: 12px; margin-bottom: 8px; border-radius: 14px; background: rgba(255,255,255,.03); border: 1px solid rgba(148,163,184,.18); box-shadow: none; transition: border-color .2s ease, box-shadow .2s ease; }
.ld04-root .ld04-sess:hover { border-color: rgba(45,212,191,.6); box-shadow: 0 0 22px rgba(45,212,191,.16); }
.ld04-sess-ico { width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: rgba(45,212,191,.1); border: 1px solid rgba(45,212,191,.25); }
.ld04-sess-info { flex: 1; min-width: 0; }
.ld04-sess-name { margin: 0; font-size: .87rem; font-weight: 600; color: ${C.text}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ld04-sess-meta { display: flex; align-items: center; gap: 4px; margin-top: 3px; min-width: 0; font-size: .73rem; color: ${C.muted}; }
.ld04-sess-ip { color: ${C.faint}; margin-left: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ld04-root .ld04-signout.ui-btn { min-width: 84px; height: 44px; padding: 0 14px; border-radius: 10px; font-size: .75rem; font-weight: 700; background: #dc2626; color: #fff; box-shadow: 0 0 0 1px rgba(248,113,113,.4); flex-shrink: 0; }
.ld04-root .ld04-signout.ui-btn:hover:not(:disabled) { opacity: 1; background: #b91c1c; box-shadow: 0 0 18px rgba(220,38,38,.5); }
.ld04-root .ld04-signout.ui-btn:disabled { opacity: 1; background: rgba(220,38,38,.35); }
.ld04-dlg-foot { padding: 4px 20px 20px; }
.ld04-root .ld04-cancel.ui-btn { width: 100%; height: 46px; border-radius: 12px; font-weight: 600; background: transparent; color: ${C.muted}; border-color: rgba(148,163,184,.22); }
.ld04-root .ld04-cancel.ui-btn:hover { background: rgba(45,212,191,.08); border-color: rgba(45,212,191,.5); color: ${C.tealSoft}; }

/* ── small screens ── */
.ld04-label-row .ld04-link { margin-top: -14px; margin-bottom: -14px; }
.ld04-signup .ld04-link { margin-top: -12px; margin-bottom: -12px; }

/* phones / small tablets: 16px inputs (no iOS focus-zoom), tighter pill and dialog rows */
@media (max-width: 767px) {
  .ld04-root .ld04-input.ui-input { font-size: 16px; }
}
@media (max-width: 400px) {
  .ld04-root .ld04-pill { font-size: .62rem; padding: 6px 12px; }
  .ld04-root .ld04-org-card { gap: 10px; padding: 10px 12px; }
  .ld04-root .ld04-org-ico { width: 34px; height: 34px; }
  .ld04-dlg-banner { padding: 20px 18px 16px; }
  .ld04-dlg-body { padding: 14px 14px 6px; }
  .ld04-dlg-foot { padding: 4px 14px 16px; }
  .ld04-root .ld04-sess { gap: 10px; padding: 10px; }
  .ld04-root .ld04-signout.ui-btn { min-width: 76px; padding: 0 10px; }
}

/* short viewports (landscape phones): compact vertical rhythm, page scrolls if still too tall */
@media (max-height: 520px) {
  .ld04-page { padding-top: 12px; }
  .ld04-brand { margin-bottom: 12px; }
  .ld04-logo { height: 32px; }
  .ld04-root .ld04-pill { display: none; }
  .ld04-root .ld04-ch { padding-top: 20px; }
  .ld04-root .ld04-cc { padding-top: 14px; }
  .ld04-root .ld04-cf { padding-top: 10px; padding-bottom: 18px; }
  .ld04-root .ld04-title { font-size: 1.4rem; margin-bottom: 4px; }
  .ld04-chips { display: none !important; }
  .ld04-footer { margin-top: 14px; }
  .ld04-ground { display: none; }
  .ld04-dlg-banner { padding: 16px 20px 14px; }
}
/* landscape phone: brand beside the form instead of stacked above it */
@media (max-height: 520px) and (min-width: 560px) and (orientation: landscape) {
  .ld04-stage { max-width: 880px; display: grid; grid-template-columns: minmax(120px, 1fr) minmax(0, 400px); column-gap: clamp(24px, 5vw, 56px); align-items: center; }
  .ld04-brand { grid-column: 1; grid-row: 1; margin-bottom: 0; }
  .ld04-card-wrap { grid-column: 2; grid-row: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .ld04-root *, .ld04-root *::before, .ld04-root *::after { animation: none !important; transition: none !important; }
}
`;

function LoginDesign04({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();

  const reduce = useReducedMotion();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: C.teal }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: C.teal }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: C.teal }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: C.teal }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  // ───────────────────────── Visual layer only ─────────────────────────

  // Entrance props. Each element animates itself with explicit initial/animate (no parent
  // variants to inherit), so nothing can get stuck hidden. Anything functional (card, inputs,
  // buttons) only animates position and never opacity, so it stays visible even if an
  // animation frame never runs. Reduced motion renders everything in place instantly.
  const rise = (delay = 0) => ({
    initial: reduce ? false : { y: 16 },
    animate: { y: 0 },
    transition: reduce ? { duration: 0 } : { duration: 0.6, delay, ease: EASE },
  });
  const enter = (delay = 0) => ({
    initial: reduce ? false : { opacity: 1, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: reduce ? { duration: 0 } : { duration: 0.65, delay, ease: EASE },
  });
  const viewExit = reduce ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, y: -10, transition: { duration: 0.18 } };
  const hoverLift = reduce ? undefined : { y: -2 };
  const tapPress = reduce ? undefined : { scale: 0.98 };

  const features = [
    "Multi-company ledger management",
    "GST-compliant invoicing & filings",
    "Real-time cash flow analytics",
    "Role-based access & audit trail",
  ];

  const capsHelper = capsLockOn
    ? <><WarningAmberIcon sx={{ fontSize: 14 }} />Caps Lock is ON</>
    : " ";

  // Animated error alert (slide in + shake; plain fade when reduced motion)
  const renderError = () => (
    <AnimatePresence initial={false}>
      {errorMessage && (
        <motion.div
          key="ld04-error"
          className="ld04-alert-wrap"
          initial={{ opacity: 1, height: 0, marginBottom: 0 }}
          animate={{
            opacity: 1,
            height: "auto",
            marginBottom: 20,
            x: reduce ? 0 : [0, -6, 6, -4, 4, 0],
          }}
          exit={{ opacity: 0, height: 0, marginBottom: 0 }}
          transition={reduce ? { duration: 0 } : { duration: 0.25, x: { duration: 0.45, ease: "easeInOut" } }}
        >
          <Alert variant="destructive" className="ld04-alert">
            <WarningAmberIcon className="ld04-alert-ico" aria-hidden="true" />
            <span>{errorMessage}</span>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <ThemeScope vars={THEME_VARS} className="ld04-root ld04-page">
      <style>{CSS}</style>

      {/* ── BACKDROP (decorative) ── */}
      <div className="ld04-bg" aria-hidden="true">
        <div className="ld04-grid" />
        <div className="ld04-aurora ld04-aurora-a" />
        <div className="ld04-aurora ld04-aurora-b" />
        {STARS.map(([t, l, s, d], i) => (
          <div key={i} className="ld04-star" style={{
            top: t + "%", left: l + "%", width: s, height: s,
            background: i % 3 === 0 ? C.teal : "#cfe3ff",
            animation: "ld04Twinkle " + (4 + (i % 4)) + "s ease-in-out " + d + "s infinite",
          }} />
        ))}
      </div>

      {/* ── CENTER STAGE ── */}
      <main className="ld04-stage">

        {/* Brand */}
        <motion.div className="ld04-brand" {...enter(0.05)}>
          <img className="ld04-logo" src="/RBLOGOBLUE.svg" alt="ReonBook" />
          <Badge variant="outline" className="ld04-pill">
            <span className="ld04-pill-dot" aria-hidden="true" />
            Enterprise Accounting Platform
          </Badge>
        </motion.div>

        {/* Floating card w/ gradient border */}
        <motion.div className="ld04-card-wrap" {...rise(0.12)}>
          <div className="ld04-ground" aria-hidden="true" />

          <motion.div
            animate={reduce ? undefined : { y: [0, -9, 0] }}
            transition={{ duration: 0.01, repeat: 0, ease: "easeInOut" }}
          >
            <motion.div
              className="ld04-frame"
              animate={reduce ? undefined : { backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
              transition={{ duration: 0.01, repeat: 0, ease: "easeInOut" }}
            >
              <Card className="ld04-card">
                <AnimatePresence mode="wait">
                  {!isCompanySelectionVisible ? (

                    /* ── LOGIN FORM ── */
                    <motion.div key="ld04-login" exit={viewExit}>
                      <CardHeader className="ld04-ch">
                        <motion.div {...rise(0.1)}>
                          <CardTitle role="heading" aria-level={1} className="ld04-title">
                            Welcome <span className="ld04-grad">back</span>
                          </CardTitle>
                          <CardDescription className="ld04-desc">
                            Sign in to access your ReonBook dashboard
                          </CardDescription>
                        </motion.div>
                      </CardHeader>

                      <form noValidate onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }}>
                        <CardContent className="ld04-cc">
                          <motion.div {...rise(0.18)}>
                            <Label htmlFor="ld04-identifier" className="ld04-label">Username, Email or Mobile</Label>
                            <div className="ld04-field">
                              <PersonOutlineRoundedIcon className="ld04-ico" aria-hidden="true" />
                              <Input
                                id="ld04-identifier"
                                name="identifier"
                                className="ld04-input ld04-input-id"
                                value={identifier}
                                placeholder="Enter your username, email or mobile"
                                autoComplete="username"
                                autoCapitalize="none"
                                spellCheck={false}
                                onChange={(e) => setIdentifier(e.target.value)}
                                onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                              />
                              {capsLockOn && <WarningAmberIcon className="ld04-warn-ico" style={{ right: 14 }} aria-hidden="true" />}
                            </div>
                            <div className="ld04-help" aria-live="polite">{capsHelper}</div>
                          </motion.div>

                          <motion.div {...rise(0.26)}>
                            <div className="ld04-label-row">
                              <Label htmlFor="ld04-password" className="ld04-label">Password</Label>
                              <Link to="/forgot-password" className="ld04-link" style={{ fontSize: "0.78rem" }}>
                                Forgot password?
                              </Link>
                            </div>
                            <div className="ld04-field">
                              <LockOutlinedIcon className="ld04-ico" aria-hidden="true" />
                              <Input
                                id="ld04-password"
                                name="password"
                                className="ld04-input ld04-input-pw"
                                type={showPassword ? "text" : "password"}
                                value={userPassword}
                                placeholder="Enter your password"
                                autoComplete="current-password"
                                onChange={(e) => setUserPassword(e.target.value)}
                                onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                              />
                              {capsLockOn && <WarningAmberIcon className="ld04-warn-ico" style={{ right: 48 }} aria-hidden="true" />}
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="ld04-eye"
                                onClick={handleTogglePasswordVisibility}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                aria-pressed={showPassword}
                              >
                                {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                              </Button>
                            </div>
                            <div className="ld04-help ld04-forgot-gap" aria-live="polite">{capsHelper}</div>
                          </motion.div>

                          {renderError()}
                        </CardContent>

                        <CardFooter className="ld04-cf">
                          <motion.div {...rise(0.34)}>
                            <MotionButton
                              type="submit"
                              size="lg"
                              className="ld04-btn-primary"
                              disabled={isLoading}
                              whileHover={isLoading ? undefined : hoverLift}
                              whileTap={isLoading ? undefined : tapPress}
                            >
                              {isLoading
                                ? <span className="ld04-btn-inner"><CircularProgress size={18} thickness={5} sx={{ color: "#c9fff7" }} />Signing in...</span>
                                : "Sign In"}
                            </MotionButton>

                            {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                              <MotionButton
                                type="button"
                                variant="outline"
                                className="ld04-btn-ghost-teal"
                                whileHover={hoverLift}
                                whileTap={tapPress}
                                onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}
                              >
                                Login with Biometric
                              </MotionButton>
                            )}
                          </motion.div>

                          <motion.div {...rise(0.4)}>
                            <Separator className="ld04-sep" style={{ marginTop: 24 }} />
                            <p className="ld04-signup">
                              Don't have an account?{" "}
                              <Link to="/register" className="ld04-link" style={{ fontWeight: 700 }}>
                                Sign up now
                              </Link>
                            </p>
                          </motion.div>
                        </CardFooter>
                      </form>
                    </motion.div>

                  ) : (

                    /* ── COMPANY SELECTION ── */
                    <motion.div key="ld04-orgs" exit={viewExit}>
                      <CardHeader className="ld04-ch">
                        <motion.div {...rise(0.05)}>
                          <CardTitle role="heading" aria-level={1} className="ld04-title">
                            Select <span className="ld04-grad">Organization</span>
                          </CardTitle>
                          <CardDescription className="ld04-desc">
                            Choose which organization to access
                          </CardDescription>
                        </motion.div>
                      </CardHeader>

                      <CardContent className="ld04-cc">
                        <Label id="ld04-org-label" className="ld04-label">Choose your organization</Label>
                        <div className="ld04-orgs" role="radiogroup" aria-labelledby="ld04-org-label">
                          {companies.map((c, i) => (
                            <motion.label
                              key={c.companyId}
                              className="ld04-org"
                              {...rise(0.1 + i * 0.06)}
                              whileHover={hoverLift}
                              whileTap={tapPress}
                            >
                              <input
                                type="radio"
                                name="ld04-company"
                                className="ld04-org-input"
                                value={c.companyId}
                                checked={(selectedCompany?.companyId || "") === c.companyId}
                                onChange={() => setSelectedCompany(companies.find((x) => x.companyId === c.companyId))}
                              />
                              <Card className="ld04-org-card">
                                <span className="ld04-org-ico" aria-hidden="true"><BusinessOutlinedIcon sx={{ fontSize: 20 }} /></span>
                                <span className="ld04-org-name">{c.companyName}</span>
                                <span className="ld04-radio" aria-hidden="true"><CheckIcon sx={{ fontSize: 14 }} /></span>
                              </Card>
                            </motion.label>
                          ))}
                        </div>

                        {renderError()}
                      </CardContent>

                      <CardFooter className="ld04-cf" style={{ paddingTop: 0 }}>
                        <motion.div {...rise(0.2)}>
                          <MotionButton
                            type="button"
                            size="lg"
                            className="ld04-btn-primary"
                            onClick={() => FN_SelectCompany()}
                            whileHover={hoverLift}
                            whileTap={tapPress}
                          >
                            Continue to Dashboard
                            <ArrowForwardIcon sx={{ fontSize: 18 }} />
                          </MotionButton>
                        </motion.div>
                      </CardFooter>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Feature chips */}
        <ul className="ld04-chips" aria-label="Platform highlights">
          {features.map((feat, i) => (
            <motion.li
              key={feat}
              {...enter(0.7 + i * 0.08)}
              whileHover={hoverLift}
            >
              <Badge variant="outline" className="ld04-chip">
                <span className="ld04-chip-check" aria-hidden="true"><CheckIcon sx={{ fontSize: 10 }} /></span>
                {feat}
              </Badge>
            </motion.li>
          ))}
        </ul>
      </main>

      {/* ── FOOTER ── */}
      <motion.p
        className="ld04-footer"
        initial={reduce ? false : { opacity: 1, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: reduce ? 0 : 0.9, ease: EASE }}
      >
        <ShieldOutlinedIcon sx={{ fontSize: 13, color: "rgba(45,212,191,0.7)" }} aria-hidden="true" />
        <span>
          <CopyrightIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.4 }} />
          {new Date().getFullYear()} Powered by{" "}
          {/* <a href="https://soltrack.co.in/" target="_blank" rel="noopener noreferrer" style={{ color: "#475569", fontWeight: 600, textDecoration: "none" }}> */}
            Soltrack Technologies Pvt Ltd
          {/* </a> */}
        </span>
      </motion.p>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="ld04-root"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Grow}
        transitionDuration={{ enter: 320, exit: 180 }}
        BackdropProps={{ sx: { backdropFilter: "blur(6px)", bgcolor: "rgba(2,6,18,0.78)" } }}
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 4 },
            width: { xs: "calc(100% - 32px)", sm: "100%" },
            maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" },
            minWidth: 0,
            borderRadius: "22px",
            overflow: "hidden",
            backgroundImage: "none",
            bgcolor: C.card,
            color: C.text,
            border: "1px solid rgba(45,212,191,0.32)",
            boxShadow: "0 30px 90px rgba(0,0,0,0.75), 0 0 60px -10px rgba(45,212,191,0.25)",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ThemeScope vars={THEME_VARS} className="ld04-root">
            {/* Top banner */}
            <div className="ld04-dlg-banner">
              <div className="ld04-dlg-head">
                <span className="ld04-dlg-badge"><DevicesIcon sx={{ fontSize: 22 }} /></span>
                <div>
                  <h2 className="ld04-dlg-title">Session Limit Reached</h2>
                  <p className="ld04-dlg-sub">
                    {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                  </p>
                </div>
              </div>
              <p className="ld04-dlg-note">Sign out of one of the devices below to log in on this device.</p>
            </div>

            {/* Device list */}
            <div className="ld04-dlg-body">
              <p className="ld04-dlg-eyebrow">Active Sessions</p>

              {sessionPickerData?.activeSessions?.map((session, idx) => (
                <MotionCard
                  key={session.id}
                  className="ld04-sess"
                  initial={reduce ? false : { opacity: 1, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: reduce ? 0 : 0.45, delay: reduce ? 0 : 0.1 + idx * 0.07, ease: EASE }}
                  whileHover={hoverLift ? { y: -1 } : undefined}
                >
                  {/* Icon */}
                  <span className="ld04-sess-ico">{deviceIcon(session.device_type)}</span>

                  {/* Info */}
                  <div className="ld04-sess-info">
                    <p className="ld04-sess-name">{session.device_name || "Unknown Device"}</p>
                    <div className="ld04-sess-meta">
                      <AccessTimeIcon sx={{ fontSize: 12, color: C.faint }} />
                      <span>{timeAgo(session.last_activity)}</span>
                      {session.ip_address && <span className="ld04-sess-ip">· {session.ip_address}</span>}
                    </div>
                  </div>

                  {/* Action */}
                  <MotionButton
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="ld04-signout"
                    disabled={kickingSessionId === session.id}
                    whileTap={tapPress}
                    onClick={async () => {
                      setKickingSessionId(session.id);
                      setSessionPickerData(null);
                      await FN_HandleLogin(identifier, userPassword, session.id);
                      setKickingSessionId(null);
                    }}
                  >
                    {kickingSessionId === session.id
                      ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                      : "Sign Out"}
                  </MotionButton>
                </MotionCard>
              ))}
            </div>

            {/* Cancel */}
            <div className="ld04-dlg-foot">
              <Button type="button" variant="outline" className="ld04-cancel" onClick={() => setSessionPickerData(null)}>
                Cancel
              </Button>
            </div>
          </ThemeScope>
        </DialogContent>
      </Dialog>

    </ThemeScope>
  );
}

export default LoginDesign04;
